if (!customElements.get('drop-a-hint')) {
  customElements.define(
    'drop-a-hint',
    class DropAHint extends HTMLElement {
      connectedCallback() {
        if (this._detachTimer) {
          clearTimeout(this._detachTimer);
          this._detachTimer = null;
        }
        this.trigger = this.querySelector('[data-dah-open]');
        if (!this.trigger) return;
        const popupId = this.trigger.getAttribute('aria-controls');
        this.popup =
          this.querySelector('[data-dah-popup]') ||
          (popupId ? document.getElementById(popupId) : null) ||
          this.popup;
        if (!this.popup) return;

        if (this.popup.parentElement !== document.body) {
          document.body.appendChild(this.popup);
        }

        this.form = this.popup.querySelector('[data-dah-form]');
        this.statusEl = this.popup.querySelector('[data-dah-status]');
        this.submitBtn = this.popup.querySelector('[data-dah-submit]');
        this.submitLabel = this.popup.querySelector('[data-dah-submit-label]');
        this.counter = this.popup.querySelector('[data-dah-counter]');
        this.message = this.popup.querySelector('[data-dah-message]');
        this.recipientPreview = this.popup.querySelector('[data-dah-preview-recipient]');
        this.senderPreview = this.popup.querySelector('[data-dah-preview-sender]');

        if (this._bound) return;
        this._bound = true;

        this.onTriggerClick = this.open.bind(this);
        this.onPopupClick = this.handlePopupClick.bind(this);
        this.onKeyDown = this.handleKeyDown.bind(this);
        this.onSubmit = this.handleSubmit.bind(this);
        this.onInput = this.handleInput.bind(this);

        this.trigger.addEventListener('click', this.onTriggerClick);
        this.popup.addEventListener('click', this.onPopupClick);
        if (this.form) {
          this.form.addEventListener('submit', this.onSubmit);
          this.form.addEventListener('input', this.onInput);
        }
      }

      disconnectedCallback() {
        this._detachTimer = setTimeout(() => {
          if (this.isConnected) return;
          this._bound = false;
          this.trigger?.removeEventListener('click', this.onTriggerClick);
          this.popup?.removeEventListener('click', this.onPopupClick);
          this.form?.removeEventListener('submit', this.onSubmit);
          this.form?.removeEventListener('input', this.onInput);
          document.removeEventListener('keydown', this.onKeyDown);
          this.popup?.remove();
          document.body.classList.remove('op-dah-lock');
        }, 0);
      }

      handlePopupClick(event) {
        const close = event.target.closest('[data-dah-close]');
        const back = event.target.closest('[data-dah-back]');
        const preview = event.target.closest('[data-dah-preview-view]');
        const formView = event.target.closest('[data-dah-form-view]');

        if (close) {
          this.close();
          return;
        }
        if (back) {
          if (this.popup.classList.contains('op-dah--mobile-preview') && window.matchMedia('(max-width: 749px)').matches) {
            this.popup.classList.remove('op-dah--mobile-preview');
            return;
          }
          this.close();
          return;
        }
        if (preview) {
          this.popup.classList.add('op-dah--mobile-preview');
          return;
        }
        if (formView) {
          this.popup.classList.remove('op-dah--mobile-preview');
        }
      }

      handleKeyDown(event) {
        if (event.key === 'Escape') this.close();
      }

      handleInput(event) {
        const field = event.target;
        if (field.name === 'recipient_name' && this.recipientPreview) {
          this.recipientPreview.textContent = field.value.trim() || '_______';
        }
        if (field.name === 'sender_name' && this.senderPreview) {
          this.senderPreview.textContent = field.value.trim() || '_______';
        }
        if (field === this.message && this.counter) {
          this.counter.textContent = `${field.value.length}/200`;
        }
        field.classList.remove('op-dah__input--error');
      }

      open() {
        this.popup.hidden = false;
        this.popup.classList.remove('op-dah--mobile-preview');
        document.body.classList.add('op-dah-lock');
        document.addEventListener('keydown', this.onKeyDown);
        this.trigger.setAttribute('aria-expanded', 'true');
        const firstInput = this.form?.querySelector('input:not([type="hidden"])');
        firstInput?.focus();
      }

      close() {
        this.popup.hidden = true;
        this.popup.classList.remove('op-dah--mobile-preview');
        document.body.classList.remove('op-dah-lock');
        document.removeEventListener('keydown', this.onKeyDown);
        this.trigger.setAttribute('aria-expanded', 'false');
        this.trigger.focus();
      }

      setStatus(message, type) {
        if (!this.statusEl) return;
        this.statusEl.hidden = !message;
        this.statusEl.textContent = message || '';
        this.statusEl.classList.remove('op-dah__status--error', 'op-dah__status--success');
        if (type) this.statusEl.classList.add(`op-dah__status--${type}`);
      }

      validate() {
        const required = this.form.querySelectorAll('[required]');
        let valid = true;
        required.forEach((field) => {
          const empty = !field.value.trim();
          const badEmail = field.type === 'email' && field.value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value.trim());
          if (empty || badEmail) {
            field.classList.add('op-dah__input--error');
            valid = false;
          } else {
            field.classList.remove('op-dah__input--error');
          }
        });
        return valid;
      }

      loadEmailJs() {
        if (window.emailjs) return Promise.resolve(window.emailjs);
        if (this.emailJsPromise) return this.emailJsPromise;

        this.emailJsPromise = new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@4/dist/email.min.js';
          script.async = true;
          script.onload = () => resolve(window.emailjs);
          script.onerror = () => reject(new Error('Could not load EmailJS'));
          document.head.appendChild(script);
        });

        return this.emailJsPromise;
      }

      async handleSubmit(event) {
        event.preventDefault();
        this.setStatus('');

        if (!this.validate()) {
          this.setStatus('Please fill in the required fields.', 'error');
          return;
        }

        const publicKey = this.dataset.publicKey?.trim();
        const serviceId = this.dataset.serviceId?.trim();
        const templateId = this.dataset.templateId?.trim();

        if (!publicKey || !serviceId || !templateId) {
          this.setStatus('Drop a Hint is not configured yet. Add your EmailJS keys in Theme settings.', 'error');
          return;
        }

        const data = new FormData(this.form);
        const params = {
          recipient_name: data.get('recipient_name') || '',
          recipient_email: data.get('recipient_email') || '',
          to_email: data.get('recipient_email') || '',
          sender_name: data.get('sender_name') || '',
          sender_email: data.get('sender_email') || '',
          sender_ring_size: data.get('sender_ring_size') || '',
          sender_message: data.get('sender_message') || '',
          product_title: data.get('product_title') || '',
          product_image: data.get('product_image') || '',
          product_url: data.get('product_url') || '',
          shop_name: data.get('shop_name') || '',
        };

        this.submitBtn.disabled = true;
        if (this.submitLabel) this.submitLabel.textContent = 'Sending...';

        try {
          const emailjs = await this.loadEmailJs();
          emailjs.init({ publicKey });
          await emailjs.send(serviceId, templateId, params);
          this.setStatus('Your hint has been sent.', 'success');
          this.form.reset();
          if (this.recipientPreview) this.recipientPreview.textContent = '_______';
          if (this.senderPreview) this.senderPreview.textContent = '_______';
          if (this.counter) this.counter.textContent = '0/200';
          if (this.submitLabel) this.submitLabel.textContent = 'Sent';
        } catch (error) {
          console.error('Drop a Hint failed', error);
          this.setStatus('Could not send the hint. Please try again.', 'error');
          if (this.submitLabel) this.submitLabel.textContent = 'Send Hint';
        } finally {
          this.submitBtn.disabled = false;
          setTimeout(() => {
            if (this.submitLabel && this.submitLabel.textContent === 'Sent') {
              this.submitLabel.textContent = 'Send Hint';
            }
          }, 2500);
        }
      }
    }
  );
}
